import React from 'react';
import Icon from './Icons';

// Animated product vignettes for the public landing (Landing.js). All pure
// CSS/SVG against the design tokens — no images, no JS animation. Every scene
// is aria-hidden decoration; the adjacent copy carries the meaning. Keyframes
// live in Landing.css and are disabled by the global reduced-motion kill
// switch in styles/motion.css.

// ---- Contract lifecycle band -------------------------------------------------
// Draft → Negotiate → Screen → Execute → Archive, with a pulse traveling the
// connector and legal/business chips under each stage.
const LIFECYCLE = [
  { icon: 'pen', name: 'Draft', legal: 'AI first draft', biz: 'Term sheet in' },
  { icon: 'list', name: 'Negotiate', legal: 'Clause redlines', biz: 'Deal points register' },
  { icon: 'shield', name: 'Screen', legal: 'Jurisdiction check', biz: 'Risk review' },
  { icon: 'doc', name: 'Execute', legal: 'Tracked-changes export', biz: 'Signature-ready' },
  { icon: 'key', name: 'Archive', legal: 'Sealed checkpoint', biz: 'Audit trail kept' },
];

export function LifecycleBand() {
  return (
    <div className="fl-lc" aria-hidden="true">
      <div className="fl-lc-stages">
        {LIFECYCLE.map((s, i) => (
          <div key={s.name} className="fl-lc-stage" style={{ '--m-delay': `${i * 80}ms` }}>
            <span className="fl-lc-node"><Icon name={s.icon} size={18} /></span>
            <span className="fl-lc-name">{s.name}</span>
            <span className="fl-lc-chip legal">{s.legal}</span>
            <span className="fl-lc-chip biz">{s.biz}</span>
          </div>
        ))}
        <div className="fl-lc-track"><span className="fl-lc-pulse" /></div>
      </div>
      <div className="fl-lc-legend">
        <span><i className="fl-lc-dot legal" /> Legal terms</span>
        <span><i className="fl-lc-dot biz" /> Business points</span>
      </div>
    </div>
  );
}

// ---- Negotiation between parties --------------------------------------------
// Two party columns; the first deal point's stances slide to center and merge
// into an agreed pill (looped); the others hold open/screening states.
export function SceneNegotiation() {
  return (
    <div className="fl-scene fl-neg" aria-hidden="true">
      <div className="fl-neg-head">
        <span className="fl-neg-party"><span className="fl-scene-avatar">AC</span> Acme — GC</span>
        <span className="fl-neg-vs">Deal points · MSA v7</span>
        <span className="fl-neg-party right">Vendor counsel <span className="fl-scene-avatar alt">VC</span></span>
      </div>

      <div className="fl-neg-row">
        <span className="fl-neg-label">Payment terms</span>
        <div className="fl-neg-lane">
          <span className="fl-neg-chip left">net 30</span>
          <span className="fl-neg-chip right">net 60</span>
          <span className="fl-neg-agreed"><Icon name="key" size={11} /> Agreed — net 30, capped late fee</span>
        </div>
      </div>

      <div className="fl-neg-row">
        <span className="fl-neg-label">Liability cap</span>
        <div className="fl-neg-lane">
          <span className="fl-neg-chip left static">2× fees</span>
          <span className="fl-neg-chip right static">1× fees</span>
          <span className="fl-neg-status warn">open</span>
        </div>
      </div>

      <div className="fl-neg-row">
        <span className="fl-neg-label">Non-compete</span>
        <div className="fl-neg-lane">
          <span className="fl-neg-status info full">screening — MN §181.988</span>
        </div>
      </div>

      <div className="fl-neg-foot">
        <Icon name="sparkle" size={12} /> AI compromise proposed from the draft — filed as a reviewable edit
      </div>
    </div>
  );
}

// ---- Audit trail --------------------------------------------------------------
// Feed entries appear one by one on a loop, each attributed and timestamped.
const AUDIT_ROWS = [
  { who: 'JK', text: <>v7 published — sealed <code>ba31…9c4e</code></>, when: '2:14 PM' },
  { who: 'AI', text: <>Agent proposal filed — §4.2 payment terms</>, when: '2:16 PM', ai: true },
  { who: 'MC', text: <>Proposal applied by M. Chen</>, when: '2:21 PM' },
  { who: 'JK', text: <>Deal point locked: Payment terms</>, when: '2:22 PM' },
  { who: 'MC', text: <>Jurisdiction screen: Minnesota — 1 caution</>, when: '2:30 PM' },
  { who: 'JK', text: <>Redline exported (.docx, both drafts)</>, when: '2:41 PM' },
];

export function SceneAudit() {
  return (
    <div className="fl-scene fl-audit" aria-hidden="true">
      <div className="fl-scene-title"><Icon name="eye" size={13} /> Audit trail</div>
      {AUDIT_ROWS.map((r, i) => (
        <div key={i} className="fl-audit-row" style={{ '--row': i }}>
          <span className={`fl-scene-avatar${r.ai ? ' ai' : ''}`}>{r.who}</span>
          <span className="fl-audit-text">{r.text}</span>
          <span className="fl-audit-when">{r.when}</span>
        </div>
      ))}
    </div>
  );
}

// ---- Versioning & storage ------------------------------------------------------
// Main version column with a counterparty branch merging back, plus the two
// storage rails (S3 sealed archive, Word export).
export function SceneVersions() {
  return (
    <div className="fl-scene fl-vers" aria-hidden="true">
      <div className="fl-vers-col">
        <div className="fl-vers-card">
          <span className="fl-vers-tag">v5</span>
          <span className="fl-vers-meta">Our draft · J. Kim</span>
        </div>
        <div className="fl-vers-branch">
          <div className="fl-vers-card branch">
            <span className="fl-vers-tag alt">v6</span>
            <span className="fl-vers-meta">Vendor markup · uploaded via drop link</span>
          </div>
        </div>
        <div className="fl-vers-card current">
          <span className="fl-vers-tag">v7</span>
          <span className="fl-vers-meta">Merged + agent edits · current</span>
        </div>
      </div>
      <div className="fl-vers-rail">
        <div className="fl-vers-store">
          <Icon name="upload" size={14} />
          <div>
            <strong>Sealed JSON → S3</strong>
            <span>object-versioned archive</span>
          </div>
        </div>
        <div className="fl-vers-store">
          <Icon name="doc" size={14} />
          <div>
            <strong>Word redline export</strong>
            <span>carries both drafts</span>
          </div>
        </div>
        <div className="fl-vers-store">
          <Icon name="compare" size={14} />
          <div>
            <strong>Compare any two</strong>
            <span>AI reads the delta for your side</span>
          </div>
        </div>
      </div>
    </div>
  );
}

// ---- Immutable hash chain ------------------------------------------------------
// Mirrors backend/shared/checkpoints.js: contentHash = SHA-256(canonical
// version), chainHash = SHA-256(prev : content) from 'genesis'; verified by
// reconciling Firestore doc, S3 archive, and the chain.
const CHAIN = [
  { v: 'v5', hash: '9f2c…41e7' },
  { v: 'v6', hash: 'c88a…b03d' },
  { v: 'v7', hash: 'ba31…9c4e' },
];

export function SceneHashChain() {
  return (
    <div className="fl-scene fl-hash" aria-hidden="true">
      <div className="fl-hash-chain">
        <span className="fl-hash-genesis">genesis</span>
        {CHAIN.map((b, i) => (
          <React.Fragment key={b.v}>
            <span className="fl-hash-link">
              <svg viewBox="0 0 40 12" width="40" height="12"><path d="M0 6h32m0 0-6-5m6 5-6 5" fill="none" stroke="currentColor" strokeWidth="1.5" /></svg>
            </span>
            <div className="fl-hash-block" style={{ '--blk': i }}>
              <span className="fl-hash-v">{b.v}</span>
              <code className="fl-hash-code">{b.hash}</code>
            </div>
          </React.Fragment>
        ))}
        <span className="fl-hash-sweep" />
      </div>
      <div className="fl-hash-formula">
        <code>chainHash = sha256(prev : contentHash)</code>
      </div>
      <div className="fl-hash-recon">
        <div className="fl-hash-check" style={{ '--chk': 0 }}><span className="fl-hash-tick" /> Firestore version doc</div>
        <div className="fl-hash-check" style={{ '--chk': 1 }}><span className="fl-hash-tick" /> S3 sealed archive</div>
        <div className="fl-hash-check" style={{ '--chk': 2 }}><span className="fl-hash-tick" /> Hash chain recomputed</div>
      </div>
      <div className="fl-hash-badge"><Icon name="shield" size={13} /> Chain intact — sha256-chain-v1</div>
    </div>
  );
}
